import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo } from 'react';
import { format, isSameDay, addDays, parseISO, isBefore, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { AdminBookingResponseDto } from '@pro-sto/api-client';
import type { ServiceCenterPostResponseDto } from '@pro-sto/api-client';
import { serviceCenterGetSlots } from '@pro-sto/api-client';
import BookingCard from '../BookingCard/BookingCard';
import PostsHeader from '../PostsHeader';
import {
  CONTENT_GAP,
  DAY_MODE_CARD_WIDTH,
  PIXELS_PER_HOUR,
  PIXELS_PER_MINUTE,
  PIXELS_PER_SLOT,
  POST_COLUMN_GAP,
  REFERENCE_POST_COUNT,
  SLOT_MINUTES,
  TIME_COLUMN_WIDTH,
  TIME_ROW_HEIGHT,
  formatSlotRange,
  getBookingLayout,
} from './calendarGeometry';
import { useCalendarDrag, type CalendarReschedulePayload } from './useCalendarDrag';
import { getBookingPostUuid } from '@/features/bookings/getBookingPostUuid';
import type { WorkingHoursRange } from '@/utils/scheduleHelpers';
import './CalendarGrid.css';

export type { CalendarReschedulePayload };

export interface CalendarGridProps {
  bookings: AdminBookingResponseDto[];
  weekStart: Date;
  onBookingClick: (bookingUuid: string) => void;
  onSlotClick?: (date: Date, hour: number, postUuid?: string, minutes?: number) => void;
  onReschedule?: (payload: CalendarReschedulePayload) => Promise<boolean>;
  onInvalidReschedule?: (message: string) => void;
  workingHours: WorkingHoursRange;
  isLoading?: boolean;
  serviceCenterUuid?: string;
  serviceUuid?: string;
  viewMode?: 'day' | 'week';
  currentDate?: Date; // для режима 'day' - текущая выбранная дата
  onViewModeChange?: (mode: 'day' | 'week') => void;
  onSelectedDateChange?: (date: Date) => void;
  posts?: ServiceCenterPostResponseDto[];
  onAddPost?: () => void;
  onDeletePost?: (postUuid: string) => void;
  onTogglePostAvailability?: (postUuid: string, isAvailable: boolean) => void;
  isPostsLoading?: boolean;
}

interface BookingWithPosition extends AdminBookingResponseDto {
  top: number;
  height: number;
  dayIndex: number;
}

// Тип для хранения доступных слотов по дням и часам
interface AvailableSlots {
  [dayIndex: number]: {
    [hour: number]: boolean;
  };
}

const canRescheduleBooking = (booking: AdminBookingResponseDto): boolean => {
  return booking.status === 'pending_confirmation' || booking.status === 'confirmed';
};

const CalendarGrid: React.FC<CalendarGridProps> = ({
  bookings,
  weekStart,
  onBookingClick,
  onSlotClick,
  onReschedule,
  onInvalidReschedule,
  workingHours,
  isLoading = false,
  serviceCenterUuid,
  serviceUuid,
  viewMode = 'week',
  currentDate,
  onViewModeChange,
  onSelectedDateChange,
  posts = [],
  onAddPost,
  onDeletePost,
  onTogglePostAvailability,
  isPostsLoading = false,
}) => {
  // Текущее время — используется для линии "сейчас" в режиме 'day'
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    if (viewMode !== 'day') return;

    // Пока висели в недельном режиме, now не обновлялся. Без этого при возврате в
    // дневной вид линия "сейчас" до 30 секунд рисуется в старом месте, а если за это
    // время наступила полночь — считается, что now относится ко вчера.
    setNow(new Date());

    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, [viewMode]);

  // Состояние для хранения доступных слотов
  const [availableSlots, setAvailableSlots] = useState<AvailableSlots>({});
  // Состояние загрузки слотов для отображения скелетона
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  // Используем useRef для предотвращения дублирования запросов
  const isLoadingSlotsRef = React.useRef(false);
  // Кэш для слотов (ключ: "dateFrom_dateTo", значение: слоты)
  const slotsCache = React.useRef<Record<string, AvailableSlots>>({});
  // Ref для контейнера дней, чтобы получить его ширину
  const daysContainerRef = React.useRef<HTMLDivElement>(null);
  // Ref для скролл-контейнера календаря (внешний, немасштабируемый — на нём же измеряем
  // ширину для postColumnWidth, см. эффект ниже)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  // Стабильная ширина видимой области календаря (для расчёта ширины колонки поста)
  const [gridViewportWidth, setGridViewportWidth] = useState(0);

  // Сортируем посты по orderNumber для стабильного порядка колонок
  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => a.orderNumber - b.orderNumber);
  }, [posts]);

  // Индекс-маппинг: post_uuid -> индекс колонки (для позиционирования бронирований)
  const postIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    sortedPosts.forEach((post, index) => {
      map.set(post.uuid, index);
    });
    return map;
  }, [sortedPosts]);

  const getPostIndex = useCallback(
    (booking: AdminBookingResponseDto): number => {
      const postUuid = getBookingPostUuid(booking);
      if (!postUuid) return 0;
      return postIndexMap.get(postUuid) ?? 0;
    },
    [postIndexMap],
  );

  // Ширина колонки поста в режиме day — считается так, будто на экране всегда
  // REFERENCE_POST_COUNT постов, независимо от их реального количества. Колонка не
  // сжимается при добавлении новых постов — вместо этого при переполнении появляется
  // горизонтальная прокрутка.
  const postColumnWidth = useMemo(() => {
    if (viewMode !== 'day' || gridViewportWidth === 0) return DAY_MODE_CARD_WIDTH;

    const availableWidth = gridViewportWidth - TIME_COLUMN_WIDTH - CONTENT_GAP;
    const totalGaps = POST_COLUMN_GAP * (REFERENCE_POST_COUNT - 1);
    return (availableWidth - totalGaps) / REFERENCE_POST_COUNT;
  }, [viewMode, gridViewportWidth]);

  // Нужна ли горизонтальная прокрутка — когда реальные посты не помещаются при
  // фиксированной (reference) ширине колонки
  const needsHorizontalScroll = useMemo(() => {
    if (viewMode !== 'day' || sortedPosts.length === 0 || gridViewportWidth === 0) return false;
    const totalWidth =
      sortedPosts.length * postColumnWidth + (sortedPosts.length - 1) * POST_COLUMN_GAP;
    const availableWidth = gridViewportWidth - TIME_COLUMN_WIDTH - CONTENT_GAP;
    return totalWidth > availableWidth;
  }, [viewMode, sortedPosts.length, postColumnWidth, gridViewportWidth]);

  // Диапазон часов для отображения: рабочие часы, расширенные бронированиями текущего
  // дня, выходящими за их пределы (запись создана до смены правил графика и т.п.)
  const { minHour, maxHour, hours } = useMemo(() => {
    let min = workingHours.start;
    let max = workingHours.end;

    if (currentDate) {
      bookings.forEach((booking) => {
        const startTime = new Date(booking.start_time);
        if (!isSameDay(startTime, currentDate)) return;

        const endTime = new Date(booking.end_time);
        const startHour = startTime.getHours();

        // Запись может закончиться уже на следующий день (ночная смена) — тогда
        // getHours() вернёт 0-1 и диапазон не расширится, а карточка отрисуется
        // ниже последнего ряда, в пустоте без подписи времени.
        const crossesMidnight = !isSameDay(startTime, endTime);
        // Запись, заканчивающаяся ровно на границе часа (17:00–18:00), занимает
        // последний час 17, а не 18 — иначе добавляется лишний пустой ряд.
        const lastOccupiedHour = crossesMidnight
          ? 23
          : endTime.getMinutes() > 0
            ? endTime.getHours()
            : Math.max(endTime.getHours() - 1, startHour);

        min = Math.min(min, startHour);
        max = Math.max(max, lastOccupiedHour);
      });
    }

    const nextHours: number[] = [];
    for (let hour = min; hour <= max; hour++) {
      nextHours.push(hour);
    }

    return { minHour: min, maxHour: max, hours: nextHours };
  }, [bookings, currentDate, workingHours.end, workingHours.start]);

  const { preview, beginDrag, consumeClickIfDragged } = useCalendarDrag({
    enabled: viewMode === 'day' && Boolean(onReschedule) && !workingHours.isClosed,
    bookings,
    minHour,
    workingHours,
    postColumnWidth,
    posts: sortedPosts,
    getPostIndex,
    containerRef: daysContainerRef,
    scrollContainerRef,
    currentDate,
    onReschedule: onReschedule ?? (async () => false),
    onInvalidDrop: onInvalidReschedule,
  });

  // Функция предзагрузки слотов для соседних периодов
  const prefetchAdjacentSlots = useCallback(async (currentDateFrom: string, currentDateTo: string) => {
    if (!serviceCenterUuid || !serviceUuid) return;

    try {
      // Вычисляем длительность текущего периода
      const from = parseISO(currentDateFrom);
      const to = parseISO(currentDateTo);
      const periodDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      // Предзагружаем предыдущий период
      const prevDateFrom = format(addDays(from, -periodDays), 'yyyy-MM-dd');
      const prevDateTo = format(addDays(to, -periodDays), 'yyyy-MM-dd');
      const prevCacheKey = `${prevDateFrom}_${prevDateTo}`;

      if (!slotsCache.current[prevCacheKey]) {
        const prevResponse = await serviceCenterGetSlots({
          uuid: serviceCenterUuid,
          serviceUuid: serviceUuid,
          dateFrom: prevDateFrom,
          dateTo: prevDateTo,
        });

        // Преобразуем слоты в формат AvailableSlots
        const prevSlots: AvailableSlots = {};
        const prevPeriodStart = parseISO(prevDateFrom);
        
        prevResponse.forEach((slot: string) => {
          const slotDateTime = parseISO(slot);
          const hour = slotDateTime.getHours();
          
          // Определяем индекс дня относительно начала предыдущего периода
          let dayIndex = -1;
          for (let i = 0; i < periodDays; i++) {
            const checkDate = addDays(prevPeriodStart, i);
            if (isSameDay(slotDateTime, checkDate)) {
              dayIndex = i;
              break;
            }
          }

          if (dayIndex !== -1 && !isNaN(hour)) {
            if (!prevSlots[dayIndex]) {
              prevSlots[dayIndex] = {};
            }
            prevSlots[dayIndex][hour] = true;
          }
        });

        slotsCache.current[prevCacheKey] = prevSlots;
      }

      // Предзагружаем следующий период
      const nextDateFrom = format(addDays(from, periodDays), 'yyyy-MM-dd');
      const nextDateTo = format(addDays(to, periodDays), 'yyyy-MM-dd');
      const nextCacheKey = `${nextDateFrom}_${nextDateTo}`;

      if (!slotsCache.current[nextCacheKey]) {
        const nextResponse = await serviceCenterGetSlots({
          uuid: serviceCenterUuid,
          serviceUuid: serviceUuid,
          dateFrom: nextDateFrom,
          dateTo: nextDateTo,
        });

        // Преобразуем слоты в формат AvailableSlots
        const nextSlots: AvailableSlots = {};
        const nextPeriodStart = parseISO(nextDateFrom);
        
        nextResponse.forEach((slot: string) => {
          const slotDateTime = parseISO(slot);
          const hour = slotDateTime.getHours();
          
          // Определяем индекс дня относительно начала следующего периода
          let dayIndex = -1;
          for (let i = 0; i < periodDays; i++) {
            const checkDate = addDays(nextPeriodStart, i);
            if (isSameDay(slotDateTime, checkDate)) {
              dayIndex = i;
              break;
            }
          }

          if (dayIndex !== -1 && !isNaN(hour)) {
            if (!nextSlots[dayIndex]) {
              nextSlots[dayIndex] = {};
            }
            nextSlots[dayIndex][hour] = true;
          }
        });

        slotsCache.current[nextCacheKey] = nextSlots;
      }
    } catch (error) {
      console.warn('Failed to prefetch adjacent slots:', error);
    }
  }, [serviceCenterUuid, serviceUuid]);

  // Функция для загрузки слотов для всей недели или одного дня
  const loadAvailableSlots = useCallback(async () => {
    // Предотвращаем повторную загрузку, если уже идет загрузка
    if (isLoadingSlotsRef.current) {
      console.log('Slots are already loading, skipping duplicate request');
      return;
    }

    if (!serviceCenterUuid || !serviceUuid) {
      console.log('Missing serviceCenterUuid or serviceUuid, skipping slots loading');
      return;
    }

    // Вычисляем начало и конец периода в зависимости от режима
    let dateFrom: string;
    let dateTo: string;
    
    if (viewMode === 'day' && currentDate) {
      // В режиме 'day' загружаем только выбранный день
      dateFrom = format(currentDate, 'yyyy-MM-dd');
      dateTo = format(currentDate, 'yyyy-MM-dd');
    } else {
      // В режиме 'week' загружаем всю неделю
      const weekEnd = addDays(weekStart, 6);
      dateFrom = format(weekStart, 'yyyy-MM-dd');
      dateTo = format(weekEnd, 'yyyy-MM-dd');
    }

    const cacheKey = `${dateFrom}_${dateTo}`;
    
    // Проверяем кэш
    if (slotsCache.current[cacheKey]) {
      setAvailableSlots(slotsCache.current[cacheKey]);
      return;
    }

    isLoadingSlotsRef.current = true;
    setIsLoadingSlots(true);
    const slots: AvailableSlots = {};

    try {
      // Делаем один запрос на период (день или неделю)
      const response = await serviceCenterGetSlots({
        uuid: serviceCenterUuid,
        serviceUuid: serviceUuid,
        dateFrom: dateFrom,
        dateTo: dateTo,
      });

      // Парсим слоты и группируем их по дням
      response.forEach((timeSlot: string) => {
        const slotDate = new Date(timeSlot);
        const hour = slotDate.getHours();
        
        let dayIndex = -1;
        
        if (viewMode === 'day' && currentDate) {
          // В режиме 'day' проверяем только выбранный день
          if (isSameDay(slotDate, currentDate)) {
            dayIndex = 0; // единственная колонка
          }
        } else {
          // В режиме 'week' определяем индекс дня недели (0-6)
          for (let i = 0; i < 7; i++) {
            if (isSameDay(slotDate, addDays(weekStart, i))) {
              dayIndex = i;
              break;
            }
          }
        }

        if (dayIndex !== -1 && !isNaN(hour)) {
          if (!slots[dayIndex]) {
            slots[dayIndex] = {};
          }
          slots[dayIndex][hour] = true;
        }
      });

      setAvailableSlots(slots);
      
      // Сохраняем в кэш
      slotsCache.current[cacheKey] = slots;

      // Предзагружаем слоты для соседних периодов (асинхронно, не блокируя основной поток)
      (async () => {
        try {
          await prefetchAdjacentSlots(dateFrom, dateTo);
        } catch (err) {
          console.warn('Failed to prefetch adjacent slots:', err);
        }
      })();
    } catch (error) {
      console.error('Failed to load available slots:', error);
    } finally {
      isLoadingSlotsRef.current = false;
      setIsLoadingSlots(false);
    }
  }, [weekStart, serviceCenterUuid, serviceUuid, viewMode, currentDate, prefetchAdjacentSlots]);

  // Загружаем слоты при изменении недели или сервиса
  useEffect(() => {
    loadAvailableSlots();
  }, [loadAvailableSlots]);

  // Измеряем ширину внешнего (немасштабируемого) контейнера календаря — используется для
  // расчёта ширины колонки поста. Наблюдаем именно scrollContainerRef, а не daysContainerRef,
  // чтобы избежать петли обратной связи: daysContainerRef сам растягивается под minWidth,
  // который зависит от этой же ширины поста.
  useLayoutEffect(() => {
    const updateGridViewportWidth = () => {
      if (scrollContainerRef.current) {
        const width = scrollContainerRef.current.clientWidth;
        setGridViewportWidth((prev) => (Math.abs(prev - width) > 0.01 ? width : prev));
      }
    };

    const timeoutId = setTimeout(updateGridViewportWidth, 0);

    const resizeObserver = new ResizeObserver(updateGridViewportWidth);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    window.addEventListener('resize', updateGridViewportWidth);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateGridViewportWidth);
    };
  }, [viewMode]);

  // Рассчитываем позиции для каждого заказа выбранного дня (режим 'week' сюда уже не
  // попадает — для него есть отдельный ранний return выше с плоским списком без сетки)
  const bookingsWithPositions: BookingWithPosition[] = bookings
    .map((booking) => {
      const startTime = new Date(booking.start_time);

      if (!currentDate || !isSameDay(startTime, currentDate)) {
        return null;
      }

      // Пост не определён (ещё не загружен или не найден) — не показываем карточку в
      // чужой колонке (getPostIndex по умолчанию вернул бы 0), лучше не показать вовсе,
      // чем приписать не тому посту.
      const postUuid = getBookingPostUuid(booking);
      const postIndex = postUuid !== undefined ? postIndexMap.get(postUuid) : undefined;
      if (postIndex === undefined) {
        return null;
      }

      const { top, height } = getBookingLayout(booking.start_time, booking.end_time, minHour);

      return {
        ...booking,
        top,
        height,
        dayIndex: postIndex,
      };
    })
    .filter((b): b is BookingWithPosition => b !== null);

  // Вычисляем высоту контейнера с бронированиями
  // Добавляем дополнительный час после последнего времени для отображения завершающих заказов
  const totalHours = hours.length;
  const bookingsHeight = totalHours * PIXELS_PER_HOUR;

  // Вычисляем минимальную ширину контента в режиме day (для горизонтального скролла)
  const dayModeMinWidth = useMemo(() => {
    if (viewMode !== 'day' || sortedPosts.length === 0 || !needsHorizontalScroll) return undefined;
    return `${sortedPosts.length * postColumnWidth + (sortedPosts.length - 1) * POST_COLUMN_GAP}px`;
  }, [viewMode, sortedPosts.length, needsHorizontalScroll, postColumnWidth]);

  // Позиция линии "текущее время" — показываем только для сегодняшнего дня в пределах
  // отображаемых часов
  const nowLineTop = useMemo(() => {
    if (viewMode !== 'day' || !currentDate || !isSameDay(now, currentDate)) return null;

    const nowHour = now.getHours();
    if (nowHour < minHour || nowHour > maxHour) return null;

    const minutesFromStart = (nowHour - minHour) * 60 + now.getMinutes();
    return minutesFromStart * PIXELS_PER_MINUTE;
  }, [viewMode, currentDate, now, minHour, maxHour]);

  // Ячейки-подсказки "добавить запись" — по 15 минут, под каждым постом. Каждая ячейка —
  // отдельный элемент строго в границах своей колонки поста (не накрывает отступы между
  // постами), подсказка показывается по CSS :hover, без отслеживания позиции указателя.
  const quarterCells = useMemo(() => {
    if (!onSlotClick || !currentDate || sortedPosts.length === 0) return [];

    const cells: {
      key: string;
      top: number;
      left: number;
      height: number;
      timeLabel: string;
      onClick: () => void;
    }[] = [];

    hours.forEach((hour, hourIndex) => {
      const isAvailable = availableSlots[0]?.[hour] === true;
      // workingHours.end — час закрытия, а не последний доступный для записи.
      const isWorkingHour = hour >= workingHours.start && hour < workingHours.end;
      if (!isAvailable && !isWorkingHour) return;

      for (let quarterIndex = 0; quarterIndex < 4; quarterIndex += 1) {
        const quarterStart = new Date(currentDate);
        quarterStart.setHours(hour, quarterIndex * SLOT_MINUTES, 0, 0);
        const quarterEnd = new Date(currentDate);
        quarterEnd.setHours(hour, (quarterIndex + 1) * SLOT_MINUTES, 0, 0);

        // Предлагаем время начала ячейки — запись на уже прошедшее время создать нельзя.
        if (quarterStart <= new Date()) continue;

        const top =
          hourIndex * PIXELS_PER_HOUR + quarterIndex * (PIXELS_PER_HOUR / 4);
        const timeLabel = formatSlotRange(hour, quarterIndex * SLOT_MINUTES);

        sortedPosts.forEach((post, postIndex) => {
          const hasBookingInPost = bookingsWithPositions.some((b) => {
            if (b.dayIndex !== postIndex) return false;
            const bStart = new Date(b.start_time);
            const bEnd = new Date(b.end_time);
            return bStart < quarterEnd && bEnd > quarterStart;
          });
          if (hasBookingInPost) return;

          const left = postIndex * (postColumnWidth + POST_COLUMN_GAP);

          cells.push({
            key: `quarter-${hour}-${quarterIndex}-${post.uuid}`,
            top,
            left,
            height: PIXELS_PER_HOUR / 4,
            timeLabel,
            onClick: () =>
              onSlotClick(currentDate, quarterStart.getHours(), post.uuid, quarterStart.getMinutes()),
          });
        });
      }
    });

    return cells;
  }, [
    availableSlots,
    bookingsWithPositions,
    currentDate,
    hours,
    onSlotClick,
    postColumnWidth,
    sortedPosts,
    workingHours.end,
    workingHours.start,
  ]);

  // Режим 'week' — плоский список бронирований по дням, без привязки ко времени
  // (без часовой сетки и драг-н-дропа — это отдельный, более простой вид из редизайна).
  if (viewMode === 'week') {
    const todayStart = startOfDay(new Date());

    const handleAddBookingForDay = (dayDate: Date) => {
      if (!onSlotClick) return;
      const hour = Math.floor(workingHours.openMinutes / 60);
      const minutes = workingHours.openMinutes % 60;
      onSlotClick(dayDate, hour, undefined, minutes);
    };

    const handleSelectDay = (dayDate: Date) => {
      onSelectedDateChange?.(dayDate);
      onViewModeChange?.('day');
    };

    return (
      <div
        className={`calendar-grid calendar-grid--week ${isLoading ? 'calendar-grid--loading' : ''}`}
        data-testid="calendar-grid"
      >
        <div className="calendar-grid__week-columns" data-testid="calendar-week-columns">
          {isLoading
            ? Array.from({ length: 7 }).map((_, dayIndex) => (
                <div
                  key={dayIndex}
                  className="calendar-grid__week-column-skeleton"
                  data-testid={`calendar-week-column-skeleton-${dayIndex}`}
                />
              ))
            : Array.from({ length: 7 }).map((_, dayIndex) => {
                const dayDate = addDays(weekStart, dayIndex);
                const dayBookings = bookings
                  .filter((booking) => isSameDay(new Date(booking.start_time), dayDate))
                  .sort(
                    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
                  );
                const isPastDay = isBefore(dayDate, todayStart);
                const isCurrentDay = currentDate ? isSameDay(dayDate, currentDate) : false;
                const dayOfWeek = format(dayDate, 'EEEEEE', { locale: ru }).toUpperCase();
                const dayLabel = format(dayDate, 'd MMMM', { locale: ru });

                return (
                  <div
                    key={dayIndex}
                    className="calendar-grid__week-column"
                    data-testid={`calendar-week-column-${dayIndex}`}
                  >
                    <button
                      type="button"
                      className={`calendar-grid__day-pill ${isCurrentDay ? 'calendar-grid__day-pill_current' : ''}`}
                      onClick={() => handleSelectDay(dayDate)}
                      aria-label={`Перейти к ${dayLabel}`}
                      data-testid={`calendar-day-${dayIndex}`}
                    >
                      {dayOfWeek} {dayLabel}
                    </button>
                    {dayBookings.map((booking) => (
                      <div key={booking.uuid} className="calendar-grid__week-booking-wrapper">
                        <BookingCard
                          booking={booking}
                          variant="week"
                          onClick={() => onBookingClick(booking.uuid)}
                        />
                      </div>
                    ))}
                    {!isPastDay && onSlotClick && (
                      <button
                        type="button"
                        className="calendar-grid__week-add"
                        onClick={() => handleAddBookingForDay(dayDate)}
                        data-testid={`calendar-week-add-${dayIndex}`}
                      >
                        <span className="calendar-grid__week-add-text">Добавить запись</span>
                        <span className="calendar-grid__week-add-icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 4.5V15.5M4.5 10H15.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className={`calendar-grid ${isLoading ? 'calendar-grid--loading' : ''}`}
      data-testid="calendar-grid"
      style={{
        ['--calendar-hour-height' as string]: `${PIXELS_PER_HOUR}px`,
        ['--calendar-time-label-height' as string]: `${TIME_ROW_HEIGHT}px`,
      }}
    >
      {/* Фиксированная шапка: "Время" + PostsHeader (sticky top) */}
      {viewMode === 'day' && !workingHours.isClosed && onAddPost && onDeletePost && (
        <div className="calendar-grid__header-row" style={needsHorizontalScroll ? { minWidth: dayModeMinWidth } : undefined}>
          <div className="calendar-grid__time-column-header">Время</div>
          <div className="calendar-grid__posts-header-row">
            <PostsHeader
              posts={posts}
              onAddPost={onAddPost}
              onTogglePostAvailability={onTogglePostAvailability}
              isLoading={isPostsLoading}
              postColumnWidth={postColumnWidth}
            />
          </div>
        </div>
      )}

      <div className="calendar-grid__content" style={needsHorizontalScroll ? { minWidth: dayModeMinWidth } : undefined}>
        {viewMode === 'day' && workingHours.isClosed ? (
          <div className="calendar-grid__closed-day" data-testid="calendar-closed-day">
            <h3>Выходной день</h3>
            <p>В этот день сервисный центр не работает</p>
          </div>
        ) : (
          <>
        {/* Колонка с временными метками */}
        <div className="calendar-grid__time-column">
          {hours.map((hour) => (
            <div key={hour} className="calendar-grid__time-row" data-testid={`calendar-slot-${hour}`}>
              <span className="calendar-grid__time-label">
                {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
              </span>
            </div>
          ))}
        </div>

        {/* Сетка с днями */}
        <div className="calendar-grid__days-container" ref={daysContainerRef}>
          {isLoading || isLoadingSlots || isPostsLoading ? (
            /* Показываем скелетон на время загрузки заказов, слотов или постов — брони
               резолвятся в колонку через getBookingPostUuid/postIndexMap, и если посты
               ещё не подтянулись, а брони уже пришли, записи на миг пропадали бы из вида
               вместо того чтобы просто подождать вместе со скелетоном. */
            <div 
              className="calendar-grid__skeleton"
              style={{ height: `${bookingsHeight}px` }}
            />
          ) : (
            <>
              {/* Горизонтальные линии сетки — рисуются отдельно на каждый пост, чтобы
                  прерываться на отступах между колонками, а не идти одной сплошной
                  полосой через весь календарь. */}
              <div
                className="calendar-grid__horizontal-lines"
                style={{ height: `${bookingsHeight}px` }}
              >
                {sortedPosts.map((post, postIndex) => {
                  const left = postIndex * (postColumnWidth + POST_COLUMN_GAP);
                  return hours.map((hour, hourIndex) => (
                    <React.Fragment key={`${post.uuid}-${hour}`}>
                      <div
                        className="calendar-grid__hour-line"
                        style={{
                          top: `${hourIndex * PIXELS_PER_HOUR}px`,
                          left: `${left}px`,
                          width: `${postColumnWidth}px`,
                        }}
                      />
                      {[1, 2, 3].map((quarter) => (
                        <div
                          key={`${post.uuid}-${hour}-${quarter}`}
                          className="calendar-grid__slot-line"
                          style={{
                            top: `${hourIndex * PIXELS_PER_HOUR + quarter * PIXELS_PER_SLOT}px`,
                            left: `${left}px`,
                            width: `${postColumnWidth}px`,
                          }}
                        />
                      ))}
                    </React.Fragment>
                  ));
                })}
              </div>

              {/* Линия текущего времени */}
              {nowLineTop !== null && (
                <div
                  className="calendar-grid__now-line"
                  style={{ top: `${nowLineTop}px` }}
                  data-testid="calendar-now-line"
                />
              )}

              {/* Карточки заказов */}
              <div 
                className="calendar-grid__bookings"
                style={{
                  height: `${bookingsHeight}px`,
                  minWidth: dayModeMinWidth,
                }}
              >
                {bookingsWithPositions.map((booking) => {
                  const isPreviewed = preview?.uuid === booking.uuid;
                  const left = isPreviewed
                    ? preview.left
                    : booking.dayIndex * (postColumnWidth + POST_COLUMN_GAP);
                  const top = isPreviewed ? preview.top : booking.top;
                  const width = postColumnWidth;
                  const height = isPreviewed ? preview.height : booking.height;

                  return (
                    <div
                      key={booking.uuid}
                      className="calendar-grid__booking-wrapper"
                      style={{
                        position: 'absolute',
                        top: `${top}px`,
                        left: `${left}px`,
                        width: `${width}px`,
                        zIndex: isPreviewed ? 30 : 10,
                      }}
                    >
                      <BookingCard
                        booking={booking}
                        variant="day"
                        onClick={() => {
                          if (consumeClickIfDragged()) return;
                          onBookingClick(booking.uuid);
                        }}
                        onMovePointerDown={
                          onReschedule && canRescheduleBooking(booking)
                            ? (event) => {
                                beginDrag(event, booking, 'move', left);
                              }
                            : undefined
                        }
                        style={{ height: `${height}px` }}
                        isInvalid={isPreviewed && preview.isInvalid}
                        isDragging={isPreviewed}
                        onResizeStartPointerDown={
                          onReschedule && canRescheduleBooking(booking)
                            ? (event) => {
                                beginDrag(event, booking, 'resize', left, 'start');
                              }
                            : undefined
                        }
                        onResizeEndPointerDown={
                          onReschedule && canRescheduleBooking(booking)
                            ? (event) => {
                                beginDrag(event, booking, 'resize', left, 'end');
                              }
                            : undefined
                        }
                      />
                    </div>
                  );
                })}

                {/* Ячейки-подсказки "добавить запись" — подсказка скрыта, появляется при
                    наведении именно на эту ячейку (чисто CSS :hover, без слежения за
                    позицией курсора), строго в границах колонки своего поста. */}
                {quarterCells.map((cell) => (
                  <div
                    key={cell.key}
                    className="calendar-grid__quarter-cell"
                    style={{
                      top: `${cell.top}px`,
                      left: `${cell.left}px`,
                      width: `${postColumnWidth}px`,
                      height: `${cell.height}px`,
                    }}
                    onClick={cell.onClick}
                  >
                    <div className="calendar-grid__quarter-cell-hint">
                      <span className="calendar-grid__quarter-cell-hint-icon" aria-hidden="true">
                        +
                      </span>
                      <span>Добавить запись</span>
                      <span aria-hidden="true">•</span>
                      <span>{cell.timeLabel}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarGrid;
